/**
 * Width of the iframes that show a rendered email "as on desktop"
 * (send preview, sent-mail viewer).
 *
 * The exported email carries a `@media (max-width: 620px)` block that stacks
 * columns, widens the content table to 100 % and shrinks big headings
 * (parse-email.ts, `MOBILE_BREAKPOINT_PX`). These previews used to be a fixed
 * 600 px viewport — BELOW that breakpoint — so they always showed the mobile
 * layout: multi-column rows stacked, while the builder canvas and a desktop
 * inbox showed them side by side. Templates wider than 600 px only fit because
 * that same mobile rule squeezed them to 100 %.
 *
 * Now the frame starts at EMAIL_PREVIEW_MIN_WIDTH (above the breakpoint) and
 * grows to the email's own width when it is wider (content width is
 * configurable up to 900 px). It never shrinks while showing one email —
 * `scrollWidth` is at least the viewport — so callers reset it to the minimum
 * whenever they load a different email.
 */

export const EMAIL_PREVIEW_MIN_WIDTH = 640;


/**
 * Upper bound. Builder emails are at most 900 px wide; a raw-HTML template can
 * carry a fixed 1600 px image (the preview would shrink to a speck) or content
 * whose width follows the viewport and overflows it — every widening would
 * then measure wider again. The frame is measured ONCE per email
 * (useEmailPreviewFrame) and never past this cap.
 */
export const EMAIL_PREVIEW_MAX_WIDTH = 1200;

/** The room a desktop frame leaves beside the mail: 640 − 620, the floor's contract. */
export const EMAIL_PREVIEW_GUTTER_PX = 20;

/**
 * The breakpoint the mail ITSELF declares — the first `@media (max-width: …)`
 * in its head (parse-email's first style block comes before every other
 * query; the webfont slot holds only `@font-face`). Null for html without
 * one (a raw template).
 * Clamped to the range the export can produce (300–900 px mails → 620–920):
 * a raw import with a foreign `max-width: 1000px` query must not inflate
 * the frame. Since plan fluffy-strolling-sonnet P1.7 a mail wider than
 * 600 px declares `width + 20`, so a frame that starts at 640 would show a
 * 900 px mail STACKED — and `.row-content{width:100% !important}` squeezes
 * it to the frame, so measuring could never recover. The frame must start
 * above the mail's own breakpoint.
 */
export function exportedMobileBreakpointOf(html: string | null | undefined): number | null {
  const m = /@media \(max-width:\s*(\d+)px\)/.exec(html ?? "");
  if (!m) return null;
  return Math.min(920, Math.max(620, Number(m[1])));
}

/** The width a desktop frame or thumbnail must START at to show this mail's desktop layout. */
export function emailPreviewMinWidthFor(html: string | null | undefined): number {
  const bp = exportedMobileBreakpointOf(html);
  return Math.min(EMAIL_PREVIEW_MAX_WIDTH, Math.max(EMAIL_PREVIEW_MIN_WIDTH, (bp ?? 0) + EMAIL_PREVIEW_GUTTER_PX));
}

/**
 * The layout width the frame needs for `doc`, clamped to
 * [`minWidth` (the mail's own, see emailPreviewMinWidthFor), EMAIL_PREVIEW_MAX_WIDTH]. Measures both the root
 * and the body: the content is written with `document.write`, so builder
 * html keeps its doctype (standards mode, the root carries the overflow),
 * while a raw template without a doctype still renders in quirks mode, where
 * the body, not the root, carries it.
 */
export function measureEmailFrameWidth(doc: Document | null | undefined, minWidth = EMAIL_PREVIEW_MIN_WIDTH): number {
  const root = doc?.documentElement?.scrollWidth ?? 0;
  const body = doc?.body?.scrollWidth ?? 0;
  return Math.min(EMAIL_PREVIEW_MAX_WIDTH, Math.max(minWidth, EMAIL_PREVIEW_MIN_WIDTH, root, body));
}

/**
 * Content height for the frame (Figma backlog W1-3 / L39).
 *
 * Standards mode (builder output, doctype kept by `document.write`): html and
 * body wrap their content, so their rects are the truth. Quirks mode (a raw
 * template that never had a doctype): html and body stretch to the viewport,
 * so measuring them — or `documentElement.scrollHeight`, which the hook used
 * to read while `innerHTML` kept EVERY document in quirks mode — returns the
 * iframe's own height, and a frame sized from that can only ever grow (the
 * 800 px ratchet). There the body's children are measured instead. 800 only
 * when nothing laid out at all (jsdom, or a document without a body).
 */
export function measureEmailFrameHeight(doc: Document | null | undefined): number {
  const root = doc?.documentElement;
  const body = doc?.body;
  if (!doc || !root || !body) return 800;
  let height: number;
  if (doc.compatMode === "CSS1Compat") {
    height = Math.max(body.getBoundingClientRect().height, root.getBoundingClientRect().height);
  } else {
    const top = body.getBoundingClientRect().top;
    height = 0;
    for (const child of Array.from(body.children)) {
      height = Math.max(height, child.getBoundingClientRect().bottom - top);
    }
  }
  return Math.ceil(height) || 800;
}
